import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { isAssessmentOrchestrationUiEnabled } from '../../config/assessmentFlags';
import { listMyAssessments, resumeAssessmentAttempt } from '../../services/assessment.api';
import { isStudentAttemptInProgress } from '../../utils/assessmentLifecyclePresentation';
import { sortDashboardAssessments } from '../../utils/studentAssessmentNavigation';

export const studentAssessmentKeys = {
  all: ['student', 'assessments'],
  scope: (scope) => ['student', 'assessments', scope],
  dashboard: (limit) => ['student', 'assessments', 'dashboard', limit],
  live: (assessmentId) => ['student', 'assessment-live', assessmentId],
};

export function useStudentDashboardAssessments({ limit = 3 } = {}) {
  const enabled = isAssessmentOrchestrationUiEnabled();

  const listQuery = useQuery({
    queryKey: studentAssessmentKeys.dashboard(limit),
    queryFn: async () => {
      const [activeRes, upcomingRes] = await Promise.all([
        listMyAssessments({ scope: 'active' }),
        listMyAssessments({ scope: 'upcoming' }),
      ]);
      const byId = new Map();
      for (const item of [
        ...(activeRes?.data?.items || []),
        ...(upcomingRes?.data?.items || []),
      ]) {
        byId.set(item.assessmentId, item);
      }
      return sortDashboardAssessments([...byId.values()]).slice(0, limit);
    },
    enabled,
    staleTime: 15_000,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

  const items = (listQuery.data || []).filter(
    (item) => !['closed', 'reconciling', 'cancelled'].includes(item.status)
  );
  const resumeCandidateIds = items
    .filter((item) => item.attemptState === 'active' || item.canStart)
    .map((item) => item.assessmentId);

  const progressQueries = useQueries({
    queries: resumeCandidateIds.map((assessmentId) => ({
      queryKey: studentAssessmentKeys.live(assessmentId),
      queryFn: async () => {
        const response = await resumeAssessmentAttempt(assessmentId);
        return response?.data;
      },
      enabled: enabled && listQuery.isSuccess,
      staleTime: 15_000,
    })),
  });

  const liveViewByAssessmentId = useMemo(() => {
    const map = {};
    resumeCandidateIds.forEach((assessmentId, index) => {
      const view = progressQueries[index]?.data;
      if (view) map[assessmentId] = view;
    });
    return map;
  }, [resumeCandidateIds, progressQueries]);

  const progressByAssessmentId = useMemo(() => {
    const map = {};
    for (const [assessmentId, view] of Object.entries(liveViewByAssessmentId)) {
      if (view.state !== 'active') continue;
      const totalProblems = Array.isArray(view.problems) ? view.problems.length : 0;
      const solvedRaw = Number(view.liveScore?.solvedCount);
      const solvedCount = Number.isFinite(solvedRaw) ? solvedRaw : 0;
      map[assessmentId] = {
        solvedCount,
        totalProblems,
        percentage: totalProblems > 0 ? Math.min(100, (solvedCount / totalProblems) * 100) : 0,
      };
    }
    return map;
  }, [liveViewByAssessmentId]);

  const progressLoading = progressQueries.some((query) => query.isLoading);

  return {
    items,
    liveViewByAssessmentId,
    progressByAssessmentId,
    isLoading: listQuery.isLoading || (resumeCandidateIds.length > 0 && progressLoading),
    isEnabled: enabled,
    error: listQuery.error,
  };
}

/** First in-progress attempt for global resume affordances (guided-return landing). */
export function useStudentActiveAssessmentResume() {
  const enabled = isAssessmentOrchestrationUiEnabled();

  const listQuery = useQuery({
    queryKey: studentAssessmentKeys.scope('active-resume'),
    queryFn: async () => {
      const response = await listMyAssessments({ scope: 'active' });
      return response?.data?.items || [];
    },
    enabled,
    staleTime: 10_000,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });

  const items = listQuery.data || [];
  const sorted = sortDashboardAssessments(items);
  const primary = sorted[0] || null;

  const liveQuery = useQuery({
    queryKey: studentAssessmentKeys.live(primary?.assessmentId || 'none'),
    queryFn: async () => {
      const response = await resumeAssessmentAttempt(primary.assessmentId);
      return response?.data;
    },
    enabled: enabled && Boolean(primary?.assessmentId),
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });

  const liveView = liveQuery.data;
  const inProgress = isStudentAttemptInProgress(primary, liveView);

  const progress = useMemo(() => {
    if (!inProgress || !liveView) return null;
    const totalProblems = Array.isArray(liveView.problems) ? liveView.problems.length : 0;
    const solvedRaw = Number(liveView.liveScore?.solvedCount);
    const solvedCount = Number.isFinite(solvedRaw) ? solvedRaw : 0;
    return {
      solvedCount,
      totalProblems,
      percentage: totalProblems > 0 ? Math.min(100, (solvedCount / totalProblems) * 100) : 0,
    };
  }, [inProgress, liveView]);

  return {
    isEnabled: enabled,
    inProgress,
    item: primary,
    liveView,
    progress,
    isLoading: listQuery.isLoading,
  };
}
